import {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  memo,
} from "react";
import { Box, Typography } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import dayjs from "dayjs";
import {
  HOUR_START,
  HOUR_END,
  TOTAL_HOURS,
  SLOT_MINUTES,
  PX_PER_MIN,
  PX_PER_HOUR,
  TOTAL_HEIGHT,
  GUTTER_WIDTH,
  SNAP_MINUTES,
  SNAP_PX,
  STATUS_DOTS,
  REASON_LABELS,
  isBookingLocked,
  dateToPx,
  pxToDate,
  snapPx,
} from "./calendarGridShared";

// Desktop adaptation of the hand-built calendar grid used by the mobile web
// calendar and the React Native app. Same geometry, snapping, and drag/resize
// semantics — but mouse-driven: drag starts on a 4px movement threshold (no
// long-press), the resize handle appears on hover, and a plain click opens
// the booking. Interactions are wired through window-level mouse listeners so
// a drag keeps tracking outside the grid, exactly like the touch version.

const DRAG_THRESHOLD_PX = 4;

// ═══════════════════════════════════════════
// EVENT BLOCK
// ═══════════════════════════════════════════

const EventBlock = memo(function EventBlock({
  booking,
  colIndex,
  onPressStart,
  onResizeStart,
  onClickBooking,
  dragState, // null | { bookingId, currentTop, currentColIndex, active }
  liveDragStart,
  liveResizeEnd,
  stackIndex = 0,
  stackTotal = 1,
}) {
  const isThisDragging = dragState?.active && dragState.bookingId === booking.id;
  const endsAt = liveResizeEnd || booking.ends_at;
  const top = dateToPx(booking.starts_at);
  const durationMs = endsAt - booking.starts_at;
  const durationMin = durationMs / 60000;
  const height = Math.max(durationMin * PX_PER_MIN, 28);
  const color = booking.barber.color;
  const isTiny = durationMin <= 15;
  const isShort = durationMin <= 25;
  const isCancelled = booking.status === "cancelled";
  const canDrag = !isBookingLocked(booking.status);

  const statusDot = STATUS_DOTS[booking.status] || "#9b9b9b";

  const displayStart = isThisDragging && liveDragStart ? liveDragStart : booking.starts_at;
  const displayEnd =
    isThisDragging && liveDragStart
      ? new Date(liveDragStart.getTime() + durationMs)
      : endsAt;

  const resolvedTop = isThisDragging
    ? snapPx(dragState.currentTop)
    : top;
  const isHidden = isThisDragging && dragState.currentColIndex !== colIndex;

  // Stack offset for overlapping events — cancelled sits behind
  const STACK_OFFSET = 10;
  const stackLeft = stackTotal > 1 ? 2 + stackIndex * STACK_OFFSET : 2;
  const stackRight =
    stackTotal > 1 ? 2 + (stackTotal - 1 - stackIndex) * STACK_OFFSET : 2;

  const timeLabel = `${dayjs(displayStart).format("h:mm A")} – ${dayjs(displayEnd).format("h:mm A")}`;

  const content = isTiny ? (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: "5px",
        overflow: "hidden",
        px: "2px",
      }}
    >
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          backgroundColor: statusDot,
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" sx={ROW_TEXT_SX}>
        {timeLabel}
      </Typography>
      <Typography variant="body1" sx={ROW_TEXT_SX}>
        {booking.client.first_name} {booking.client.last_name}
      </Typography>
      <Typography variant="body2" sx={ROW_TEXT_SX}>
        {booking.service.name}
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        px: "2px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            backgroundColor: statusDot,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" sx={ROW_TEXT_SX}>
          {timeLabel}
        </Typography>
      </Box>
      {isShort ? (
        <Box sx={{ display: "flex", flexDirection: "row", gap: "5px" }}>
          <Typography variant="body1" sx={ROW_TEXT_SX}>
            {booking.client.first_name} {booking.client.last_name}
          </Typography>
          <Typography variant="body2" sx={ROW_TEXT_SX}>
            {booking.service.name}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body1" sx={ROW_TEXT_SX}>
            {booking.client.first_name} {booking.client.last_name}
          </Typography>
          <Typography variant="body2" sx={ROW_TEXT_SX}>
            {booking.service.name}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Box
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        if (!canDrag) return;
        e.preventDefault(); // no text selection while dragging
        onPressStart(booking, e.clientX, e.clientY, colIndex, top);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClickBooking(booking);
      }}
      sx={{
        position: "absolute",
        top: resolvedTop,
        left: isHidden ? -9999 : stackLeft,
        right: isHidden ? 9999 : stackRight,
        height,
        backgroundColor: isCancelled ? "#eeece8" : color.bg,
        borderLeft: `3px solid ${isCancelled ? "#c4c0b8" : color.border}`,
        borderRadius: "4px",
        px: "6px",
        py: "3px",
        overflow: "hidden",
        cursor: canDrag ? "grab" : "pointer",
        opacity: isThisDragging ? 0.65 : isCancelled ? 0.75 : 1,
        transition: isThisDragging ? "none" : "opacity 0.15s, box-shadow 0.15s",
        userSelect: "none",
        WebkitUserSelect: "none",
        zIndex: isThisDragging ? 0 : stackIndex + 1,
        boxShadow: isCancelled ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
        color: isCancelled ? "#9b9b9b" : color.text,
        // Reveal the resize handle on hover
        "&:hover": {
          boxShadow: isCancelled ? "none" : "0 2px 8px rgba(0,0,0,0.14)",
          "& .resize-handle-bar": { opacity: 1 },
        },
      }}
    >
      {content}

      {/* Resize handle — mouse down here to extend/shorten the booking */}
      {canDrag && height >= 28 && (
        <Box
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            onResizeStart(booking, e.clientY);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ns-resize",
            zIndex: 3,
          }}
        >
          <Box
            className="resize-handle-bar"
            sx={{
              width: 24,
              height: 3,
              borderRadius: 1.5,
              backgroundColor:
                liveResizeEnd != null ? "#4caf50" : "rgba(0,0,0,0.2)",
              opacity: liveResizeEnd != null ? 1 : 0,
              transition: "opacity 0.15s, background-color 0.15s",
            }}
          />
        </Box>
      )}
    </Box>
  );
});

const ROW_TEXT_SX = {
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// ═══════════════════════════════════════════
// DRAG GHOST — follows the cursor
// ═══════════════════════════════════════════

const DragGhost = ({ booking, x, y, columnWidth, liveDragStart }) => {
  if (!booking) return null;
  const durationMs = booking.ends_at - booking.starts_at;
  const height = Math.max((durationMs / 60000) * PX_PER_MIN, 28);
  const color = booking.barber.color;

  const displayStart = liveDragStart || booking.starts_at;
  const displayEnd = liveDragStart
    ? new Date(liveDragStart.getTime() + durationMs)
    : booking.ends_at;

  return (
    <Box
      sx={{
        position: "fixed",
        left: x - columnWidth / 2,
        top: y - height / 2,
        width: columnWidth - 4,
        height,
        backgroundColor: color.bg,
        borderLeft: `3px solid ${color.border}`,
        borderRadius: "4px",
        px: "6px",
        py: "3px",
        overflow: "hidden",
        opacity: 0.92,
        zIndex: 9999,
        pointerEvents: "none",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        transform: "scale(1.04)",
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 600,
          opacity: 0.75,
          lineHeight: 1.3,
          color: color.text,
        }}
      >
        {dayjs(displayStart).format("h:mm A")} –{" "}
        {dayjs(displayEnd).format("h:mm A")}
      </Typography>
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.3, color: color.text }}
      >
        {booking.client.first_name} {booking.client.last_name}
      </Typography>
    </Box>
  );
};

// ═══════════════════════════════════════════
// TIME GUTTER / GRID LINES / NOW INDICATOR
// ═══════════════════════════════════════════

const TimeGutter = () => {
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);
  return (
    <Box
      sx={{
        width: GUTTER_WIDTH,
        flexShrink: 0,
        position: "relative",
        height: TOTAL_HEIGHT,
        borderRight: "1px solid #f0f0f0",
        backgroundColor: "#fafafa",
      }}
    >
      {hours.map((h) => (
        <Box
          key={h}
          sx={{
            position: "absolute",
            top: (h - HOUR_START) * PX_PER_HOUR - 8,
            right: 8,
            width: GUTTER_WIDTH - 8,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: "#9b9b9b",
              fontWeight: 500,
              textAlign: "right",
            }}
          >
            {dayjs().hour(h).minute(0).format("h A")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const GridLines = memo(function GridLines() {
  const slots = Array.from(
    { length: TOTAL_HOURS * (60 / SLOT_MINUTES) },
    (_, i) => i,
  );
  return (
    <>
      {slots.map((i) => {
        const minutes = i * SLOT_MINUTES;
        const isHour = minutes % 60 === 0;
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: minutes * PX_PER_MIN,
              left: 0,
              right: 0,
              height: "1px",
              backgroundColor: isHour ? "#ebebeb" : "#f5f5f5",
              borderTop: isHour ? "none" : "1px dashed #f0f0f0",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </>
  );
});

const NowIndicator = ({ currentDate }) => {
  const now = new Date();
  const isToday = dayjs(currentDate).isSame(now, "day");
  if (!isToday) return null;
  const hourOfDay = now.getHours() + now.getMinutes() / 60;
  if (hourOfDay < HOUR_START || hourOfDay >= HOUR_END) return null;
  const top = dateToPx(now);
  return (
    <Box
      sx={{
        position: "absolute",
        top,
        left: 0,
        right: 0,
        zIndex: 5,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -4,
          top: -4,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#E24B4A",
        }}
      />
      <Box sx={{ height: 2, backgroundColor: "#E24B4A" }} />
    </Box>
  );
};

// ═══════════════════════════════════════════
// MAIN GRID
// ═══════════════════════════════════════════

export default function DesktopCalendarGrid({
  barbers, // visible barbers, each with a .color
  bookings,
  blockEvents, // [{ id, starts_at, ends_at, barber_user_id, blockData }]
  barberHours,
  dateOverrides,
  currentDate,
  height = 720,
  onSelectBooking,
  onSelectBlock,
  onSelectSlot, // ({ barber, time }) — empty-slot click
  onCommitMove, // async ({ booking, newStart, newEnd, targetBarber, barberChanged })
  onCommitResize, // async ({ booking, newEnd })
  onInvalidDrop, // (message)
}) {
  const gridRef = useRef(null);
  // The columns wrapper — Y math is measured against this element (NOT the
  // scroll container) so the sticky barber header's height never offsets the
  // computed time, and scrolling is accounted for by getBoundingClientRect.
  const colsRef = useRef(null);
  const columnRefs = useRef([]);
  const animFrameRef = useRef(null);

  // ── Column width from measured container width ────────────────────────
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const COLUMN_WIDTH = useMemo(() => {
    const cols = Math.max(barbers.length, 1);
    const available = Math.max(containerWidth - GUTTER_WIDTH, 0);
    return Math.max(150, Math.floor(available / cols));
  }, [containerWidth, barbers.length]);

  const totalGridWidth = COLUMN_WIDTH * barbers.length;

  // ── Drag state ─────────────────────────────────────────────────────────
  // Pending press → becomes an active drag after DRAG_THRESHOLD_PX movement.
  const pressRef = useRef(null);
  // pressRef = { booking, startX, startY, colIndex, eventTop, pointerOffsetWithinEvent }
  const [drag, setDrag] = useState(null);
  // drag = { bookingId, ghostX, ghostY, currentTop, currentColIndex, active: true }
  const didDragRef = useRef(false);

  // ── Resize state ───────────────────────────────────────────────────────
  const resizeRef = useRef(null);
  // resizeRef = { booking, startClientY, originalEndsAt, currentEndsAt }
  const [liveResize, setLiveResize] = useState(null);
  // liveResize = { bookingId, endsAt }

  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  const draggingBooking = useMemo(() => {
    if (!drag) return null;
    return bookings.find((b) => b.id === drag.bookingId) || null;
  }, [drag, bookings]);

  const getColumnIndexAtX = useCallback((clientX) => {
    for (let i = 0; i < columnRefs.current.length; i++) {
      const el = columnRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) return i;
    }
    return null;
  }, []);

  // ── Press → drag activation ────────────────────────────────────────────
  const handlePressStart = useCallback(
    (booking, clientX, clientY, colIndex, eventTop) => {
      if (resizeRef.current) return;
      const cols = colsRef.current;
      let pointerOffsetWithinEvent = 0;
      if (cols) {
        const rect = cols.getBoundingClientRect();
        const pointerPosInGrid = clientY - rect.top;
        pointerOffsetWithinEvent = Math.max(
          0,
          Math.min(pointerPosInGrid - eventTop, 60),
        );
      }
      didDragRef.current = false;
      pressRef.current = {
        booking,
        startX: clientX,
        startY: clientY,
        colIndex,
        eventTop,
        pointerOffsetWithinEvent,
      };
    },
    [],
  );

  const handleClickBooking = useCallback(
    (booking) => {
      // Suppress the click that follows a completed drag
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      onSelectBooking(booking);
    },
    [onSelectBooking],
  );

  // ── Window mouse listeners for drag (threshold + move + drop) ──────────
  useEffect(() => {
    const handleMouseMove = (e) => {
      const press = pressRef.current;

      // Activate the drag once the pointer moves past the threshold
      if (press && !drag) {
        const dist = Math.hypot(e.clientX - press.startX, e.clientY - press.startY);
        if (dist < DRAG_THRESHOLD_PX) return;
        didDragRef.current = true;
        setDrag({
          bookingId: press.booking.id,
          ghostX: e.clientX,
          ghostY: e.clientY,
          currentTop: press.eventTop,
          currentColIndex: press.colIndex,
          pointerOffsetWithinEvent: press.pointerOffsetWithinEvent,
          active: true,
        });
        return;
      }

      if (!drag) return;
      e.preventDefault();

      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      const { clientX, clientY } = e;
      animFrameRef.current = requestAnimationFrame(() => {
        const cols = colsRef.current;
        const colIndex = getColumnIndexAtX(clientX);

        // Measured against the columns wrapper every frame — its viewport rect
        // shifts as the container scrolls, so edge auto-scroll is accounted for.
        let newTop = 0;
        if (cols) {
          const rect = cols.getBoundingClientRect();
          newTop = clientY - rect.top - (drag.pointerOffsetWithinEvent || 0);
        }

        setDrag((prev) =>
          prev
            ? {
                ...prev,
                ghostX: clientX,
                ghostY: clientY,
                currentTop: Math.max(0, Math.min(newTop, TOTAL_HEIGHT)),
                currentColIndex:
                  colIndex !== null ? colIndex : prev.currentColIndex,
              }
            : prev,
        );
      });
    };

    const handleMouseUp = async () => {
      const press = pressRef.current;
      pressRef.current = null;

      if (!drag) return; // plain click — EventBlock's onClick handles it
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

      // If the drop lands outside the grid no click event follows, so the
      // suppression flag would swallow the NEXT genuine click — clear it once
      // this event cycle (mouseup → click) has finished.
      setTimeout(() => {
        didDragRef.current = false;
      }, 0);

      const booking = bookingsRef.current.find((b) => b.id === drag.bookingId);
      setDrag(null);
      if (!booking || !press) return;

      const duration = booking.ends_at - booking.starts_at;
      const snappedTop = snapPx(drag.currentTop);
      const newStart = pxToDate(snappedTop, currentDate);
      const newEnd = new Date(newStart.getTime() + duration);

      // Clamp within the visible grid window
      if (
        newStart.getHours() < HOUR_START ||
        newEnd.getHours() > HOUR_END ||
        (newEnd.getHours() === HOUR_END && newEnd.getMinutes() > 0)
      ) {
        onInvalidDrop?.("Outside working hours");
        return;
      }

      const targetBarber = barbers[drag.currentColIndex] || booking.barber;
      const barberChanged =
        String(targetBarber.user_id) !== String(booking.barber.user_id);

      // No-op drop — same slot, same barber
      if (
        !barberChanged &&
        newStart.getTime() === booking.starts_at.getTime()
      ) {
        return;
      }

      onCommitMove({ booking, newStart, newEnd, targetBarber, barberChanged });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    drag,
    currentDate,
    barbers,
    getColumnIndexAtX,
    onCommitMove,
    onInvalidDrop,
  ]);

  // ── Resize ─────────────────────────────────────────────────────────────
  const handleResizeStart = useCallback((booking, clientY) => {
    resizeRef.current = {
      booking,
      startClientY: clientY,
      originalEndsAt: new Date(booking.ends_at),
      currentEndsAt: new Date(booking.ends_at),
    };
    setLiveResize({ bookingId: booking.id, endsAt: new Date(booking.ends_at) });
  }, []);

  useEffect(() => {
    if (!liveResize) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      const ref = resizeRef.current;
      if (!ref) return;

      const deltaY = e.clientY - ref.startClientY;
      const snappedMs =
        Math.round(deltaY / SNAP_PX) * SNAP_MINUTES * 60000;

      const booking = bookingsRef.current.find(
        (b) => b.id === ref.booking.id,
      );
      if (!booking) return;

      const minEnd = new Date(
        booking.starts_at.getTime() + SNAP_MINUTES * 60000,
      );
      const maxEnd = new Date(booking.starts_at);
      maxEnd.setHours(HOUR_END, 0, 0, 0);

      const newEnd = new Date(
        Math.max(
          minEnd.getTime(),
          Math.min(ref.originalEndsAt.getTime() + snappedMs, maxEnd.getTime()),
        ),
      );

      ref.currentEndsAt = newEnd;
      setLiveResize({ bookingId: ref.booking.id, endsAt: newEnd });
    };

    const handleMouseUp = () => {
      const ref = resizeRef.current;
      resizeRef.current = null;
      setLiveResize(null);
      if (!ref) return;

      const booking = bookingsRef.current.find((b) => b.id === ref.booking.id);
      if (!booking) return;

      if (ref.currentEndsAt.getTime() === booking.ends_at.getTime()) return;

      onCommitResize({ booking, newEnd: ref.currentEndsAt });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [liveResize, onCommitResize]);

  // ── Edge auto-scroll while dragging ────────────────────────────────────
  useEffect(() => {
    if (!drag) return;
    const grid = gridRef.current;
    if (!grid) return;

    const EDGE_ZONE = 60;
    const SCROLL_SPEED = 8;

    const interval = setInterval(() => {
      const rect = grid.getBoundingClientRect();
      const { ghostX, ghostY } = drag;

      if (ghostY - rect.top < EDGE_ZONE) grid.scrollTop -= SCROLL_SPEED;
      else if (rect.bottom - ghostY < EDGE_ZONE) grid.scrollTop += SCROLL_SPEED;

      if (ghostX - rect.left < EDGE_ZONE) grid.scrollLeft -= SCROLL_SPEED;
      else if (rect.right - ghostX < EDGE_ZONE) grid.scrollLeft += SCROLL_SPEED;
    }, 16);

    return () => clearInterval(interval);
  }, [drag]);

  // ── Empty-slot click ───────────────────────────────────────────────────
  const handleColumnClick = useCallback(
    (e, barber) => {
      if (e.target !== e.currentTarget) return;
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      const cols = colsRef.current;
      if (!cols) return;
      const rect = cols.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const tappedTime = pxToDate(snapPx(relY), currentDate);
      onSelectSlot({ barber, time: tappedTime });
    },
    [currentDate, onSelectSlot],
  );

  const liveDragStart =
    drag != null
      ? pxToDate(snapPx(drag.currentTop), currentDate)
      : null;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <Box
        ref={gridRef}
        sx={{
          height,
          overflowY: "auto",
          overflowX: "auto",
          position: "relative",
          border: "1px solid #f0f0f0",
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          userSelect: drag || liveResize ? "none" : "auto",
          cursor: drag ? "grabbing" : "default",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e0e0e0",
            borderRadius: 4,
          },
        }}
      >
        {/* Sticky barber header — scrolls horizontally with the grid */}
        <Box
          sx={{
            display: "flex",
            width: totalGridWidth + GUTTER_WIDTH,
            minWidth: "100%",
            position: "sticky",
            top: 0,
            zIndex: 10,
            borderBottom: "1px solid #f0f0f0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
          }}
        >
          <Box
            sx={{
              width: GUTTER_WIDTH,
              flexShrink: 0,
              backgroundColor: "#fafafa",
              borderRight: "1px solid #f0f0f0",
            }}
          />
          {barbers.map((barber, i) => (
            <Box
              key={barber.user_id}
              ref={(el) => (columnRefs.current[i] = el)}
              sx={{
                width: COLUMN_WIDTH,
                flexShrink: 0,
                backgroundColor: barber.color.soft,
                borderTop: `3px solid ${barber.color.border}`,
                borderLeft: i > 0 ? "1px solid #f0f0f0" : "none",
                py: 1.5,
                px: 1,
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: barber.color.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {barber.first_name} {barber.last_name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Time gutter + barber columns */}
        <Box
          sx={{
            display: "flex",
            width: totalGridWidth + GUTTER_WIDTH,
            minWidth: "100%",
          }}
        >
          <TimeGutter />
          <Box
            ref={colsRef}
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              height: TOTAL_HEIGHT,
            }}
          >
            {/* Target column highlight while dragging */}
            {drag && drag.currentColIndex !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: drag.currentColIndex * COLUMN_WIDTH,
                  width: COLUMN_WIDTH,
                  height: TOTAL_HEIGHT,
                  backgroundColor: "rgba(156,106,54,0.06)",
                  borderLeft: "2px solid rgba(156,106,54,0.3)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            )}

            {/* Snap target line */}
            {drag && (
              <Box
                sx={{
                  position: "absolute",
                  top: snapPx(drag.currentTop),
                  left:
                    drag.currentColIndex !== null
                      ? drag.currentColIndex * COLUMN_WIDTH
                      : 0,
                  width: COLUMN_WIDTH,
                  height: 2,
                  backgroundColor: "#9c6a36",
                  opacity: 0.6,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
            )}

            {barbers.map((barber, colIndex) => {
              const colBookings = bookings.filter(
                (b) =>
                  b.barber.user_id === barber.user_id &&
                  dayjs(b.starts_at).isSame(currentDate, "day"),
              );
              return (
                <Box
                  key={barber.user_id}
                  onClick={(e) => handleColumnClick(e, barber)}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: colIndex * COLUMN_WIDTH,
                    width: COLUMN_WIDTH,
                    height: TOTAL_HEIGHT,
                    borderLeft: colIndex > 0 ? "1px solid #f0f0f0" : "none",
                    cursor: "cell",
                  }}
                >
                  <GridLines />
                  <NowIndicator currentDate={currentDate} />

                  {/* Gray out non-working hours (date override wins) */}
                  {(() => {
                    const overlayStyle = {
                      position: "absolute",
                      left: 0,
                      right: 0,
                      backgroundColor: "rgba(0,0,0,0.04)",
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.025) 4px, rgba(0,0,0,0.025) 8px)",
                      zIndex: 0,
                      pointerEvents: "none",
                    };

                    const override = dateOverrides[barber.user_id];
                    let effectiveStartTime, effectiveEndTime;

                    if (override) {
                      effectiveStartTime = override.start_time;
                      effectiveEndTime = override.end_time;
                    } else {
                      const hours = barberHours[barber.user_id];
                      if (!hours) return null;
                      const dayOfWeek = dayjs(currentDate).day();
                      const todayHours = hours.find(
                        (h) => h.day_of_week === dayOfWeek,
                      );
                      if (!todayHours || todayHours.is_working === 0) {
                        return (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: 0,
                              height: TOTAL_HEIGHT,
                            }}
                          />
                        );
                      }
                      effectiveStartTime = todayHours.start_time;
                      effectiveEndTime = todayHours.end_time;
                    }

                    const [startH, startM] = effectiveStartTime
                      .split(":")
                      .map(Number);
                    const [endH, endM] = effectiveEndTime
                      .split(":")
                      .map(Number);
                    const workStartPx =
                      ((startH - HOUR_START) * 60 + startM) * PX_PER_MIN;
                    const workEndPx =
                      ((endH - HOUR_START) * 60 + endM) * PX_PER_MIN;
                    return (
                      <>
                        {workStartPx > 0 && (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: 0,
                              height: workStartPx,
                            }}
                          />
                        )}
                        {workEndPx < TOTAL_HEIGHT && (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: workEndPx,
                              height: TOTAL_HEIGHT - workEndPx,
                            }}
                          />
                        )}
                      </>
                    );
                  })()}

                  {/* Bookings — locked statuses stack behind active ones */}
                  {(() => {
                    const STATUS_ORDER = {
                      cancelled: 0,
                      no_show: 1,
                      completed: 2,
                      pending: 3,
                      confirmed: 4,
                    };
                    const sorted = [...colBookings].sort(
                      (a, b) =>
                        (STATUS_ORDER[a.status] ?? 3) -
                        (STATUS_ORDER[b.status] ?? 3),
                    );

                    return sorted.map((booking, i) => {
                      const overlapping = sorted.filter(
                        (other, j) =>
                          j !== i &&
                          booking.starts_at < other.ends_at &&
                          booking.ends_at > other.starts_at,
                      );
                      const stackIndex = sorted
                        .slice(0, i)
                        .filter(
                          (other) =>
                            booking.starts_at < other.ends_at &&
                            booking.ends_at > other.starts_at,
                        ).length;
                      const stackTotal = overlapping.length + 1;

                      return (
                        <EventBlock
                          key={booking.id}
                          booking={booking}
                          colIndex={colIndex}
                          onPressStart={handlePressStart}
                          onResizeStart={handleResizeStart}
                          onClickBooking={handleClickBooking}
                          dragState={drag}
                          liveDragStart={
                            drag?.bookingId === booking.id
                              ? liveDragStart
                              : null
                          }
                          liveResizeEnd={
                            liveResize?.bookingId === booking.id
                              ? liveResize.endsAt
                              : null
                          }
                          stackIndex={stackIndex}
                          stackTotal={stackTotal}
                        />
                      );
                    });
                  })()}

                  {/* Blocks for this barber */}
                  {blockEvents
                    .filter((b) => b.barber_user_id === barber.user_id)
                    .map((block) => {
                      const top = dateToPx(block.starts_at);
                      const blockHeight = Math.max(
                        ((block.ends_at - block.starts_at) / 60000) *
                          PX_PER_MIN,
                        28,
                      );
                      const label =
                        block.blockData?.notes &&
                        block.blockData?.reason === "other"
                          ? block.blockData.notes
                          : REASON_LABELS[block.blockData?.reason] || "Blocked";
                      return (
                        <Box
                          key={block.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectBlock(block.blockData);
                          }}
                          sx={{
                            position: "absolute",
                            top,
                            left: 2,
                            right: 2,
                            height: blockHeight,
                            backgroundColor: "#f0ece8",
                            borderLeft: "3px solid #c0bbb4",
                            borderRadius: "4px",
                            px: "6px",
                            py: "3px",
                            overflow: "hidden",
                            backgroundImage:
                              "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.025) 5px, rgba(0,0,0,0.025) 10px)",
                            zIndex: 10,
                            cursor: "pointer",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <BlockIcon sx={{ fontSize: 10, color: "#9b9b9b" }} />
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: "#6b6b6b",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {label}
                              {block.blockData?.is_recurring ? " 🔁" : ""}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Drag ghost — follows the cursor above everything */}
      {drag && draggingBooking && (
        <DragGhost
          booking={draggingBooking}
          x={drag.ghostX}
          y={drag.ghostY}
          columnWidth={COLUMN_WIDTH}
          liveDragStart={liveDragStart}
        />
      )}
    </>
  );
}
